import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/lib/auth';
import { LanguageProvider } from '@/lib/i18n';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { BottomNav } from '@/components/BottomNav';
import HomePage from './pages/HomePage';
import SignupPage from './pages/SignupPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import NotificationsPage from './pages/NotificationsPage';
import SearchPage from './pages/SearchPage';
import JobeurProfilePage from './pages/JobeurProfilePage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import RequestDetailPage from './pages/RequestDetailPage';
import ServiceCreatePage from './pages/ServiceCreatePage';
import RequestCreatePage from './pages/RequestCreatePage';
import { ConversationPage, MessagesListPage } from './pages/MessagesPage';
import QuoteCreatePage from './pages/QuoteCreatePage';
import QuoteDetailPage from './pages/QuoteDetailPage';
import PaymentPage from './pages/PaymentPage';
import ReviewPage from './pages/ReviewPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import IdVerificationPage from './pages/IdVerificationPage';
import AdminPage from './pages/AdminPage';
import HowItWorksPage from './pages/HowItWorksPage';
import TrustPage from './pages/TrustPage';
import FAQPage from './pages/FAQPage';
import AmbassadorsPage from './pages/AmbassadorsPage';
import BlogPage from './pages/BlogPage';
import LegalPage from './pages/LegalPage';
import '@/App.css';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [pathname]);
  return null;
};

const Protected = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-[60vh] flex items-center justify-center">Chargement…</div>;
  return user ? children : <Navigate to="/login" replace />;
};

const AdminOnly = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-[60vh] flex items-center justify-center">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
};

const AppShell = () => {
  const location = useLocation();
  const isAuthPage = /^\/(login|signup|forgot-password)/.test(location.pathname);
  return (
    <>
      <ScrollToTop />
      {!isAuthPage && <Header />}
      <main className="min-h-[60vh] pb-20 md:pb-0">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/jobeur/:id" element={<JobeurProfilePage />} />
          <Route path="/service/:id" element={<ServiceDetailPage />} />
          <Route path="/request/:id" element={<RequestDetailPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/trust" element={<TrustPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/ambassadors" element={<AmbassadorsPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/terms" element={<LegalPage type="terms" />} />
          <Route path="/privacy" element={<LegalPage type="privacy" />} />
          <Route path="/code-of-conduct" element={<LegalPage type="code" />} />

          {/* Protected */}
          <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
          <Route path="/notifications" element={<Protected><NotificationsPage /></Protected>} />
          <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
          <Route path="/verify-id" element={<Protected><IdVerificationPage /></Protected>} />
          <Route path="/service/new" element={<Protected><ServiceCreatePage /></Protected>} />
          <Route path="/request/new" element={<Protected><RequestCreatePage /></Protected>} />
          <Route path="/messages" element={<Protected><MessagesListPage /></Protected>} />
          <Route path="/messages/:id" element={<Protected><ConversationPage /></Protected>} />
          <Route path="/quote/new" element={<Protected><QuoteCreatePage /></Protected>} />
          <Route path="/quote/:id" element={<Protected><QuoteDetailPage /></Protected>} />
          <Route path="/payment/:quoteId" element={<Protected><PaymentPage /></Protected>} />
          <Route path="/review/:paymentId" element={<Protected><ReviewPage /></Protected>} />
          <Route path="/admin" element={<AdminOnly><AdminPage /></AdminOnly>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!isAuthPage && <Footer />}
      <BottomNav />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <AppShell />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
